/**
 * StockVideoGrid
 *
 * Masonry grid for stock videos with hover-to-play previews, saved state, and
 * premium gating for interactions.
 */
import React, { useState, useRef, useEffect } from 'react';
import { Heart, Play, Download, Eye, Clock, Tag, Crown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import './MasonryLayout.css';

const StockVideoCard = React.memo(function StockVideoCard({ stockVideo, onStockVideoClick, onSaveStockVideo, isSaved, onImageLoaded, isPremiumUser = true }) {
  const [isHovered, setIsHovered] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [thumbnailError, setThumbnailError] = useState(false);
  const [generatedThumbnail, setGeneratedThumbnail] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isImagePreloaded, setIsImagePreloaded] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const videoRef = useRef(null);
  const cardRef = useRef(null);
  const hoverTimeoutRef = useRef(null);
  const navigate = useNavigate();

  // Convert Firebase Storage URLs
  const convertFirebaseStorageUrl = (url) => {
    if (!url) return url;
    if (url.startsWith('gs://')) {
      // Convert gs://bucket/path to https://firebasestorage.googleapis.com/v0/b/bucket/o/encodedPath?alt=media
      const urlParts = url.replace('gs://', '').split('/');
      const bucket = urlParts[0];
      const path = urlParts.slice(1).join('/');
      const encodedPath = encodeURIComponent(path);
      return `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodedPath}?alt=media`;
    }
    return url;
  };

  const convertedVideoUrl = convertFirebaseStorageUrl(stockVideo.videoUrl);

  // Check if browser supports WebP for better image compression
  const supportsWebP = () => {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    return canvas.toDataURL('image/webp').indexOf('image/webp') === 5;
  };

  // Optimize image URL for better performance
  const getOptimizedImageUrl = (url) => {
    if (!url) return url;
    
    // If it's a Firebase Storage URL, we could add compression parameters
    if (url.includes('firebasestorage.googleapis.com')) {
      // Add image optimization parameters if available
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}w=400&h=300&f=auto&q=80`; // Auto format, 80% quality, 400x300 max
    }
    
    return url;
  };

  // Intersection Observer for lazy loading
  useEffect(() => {
    const currentCardRef = cardRef.current;
    if (!currentCardRef) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.unobserve(entry.target); // Stop observing once in view
          }
        });
      },
      {
        rootMargin: '50px', // Start loading 50px before element comes into view
        threshold: 0.1
      }
    );

    observer.observe(currentCardRef);

    return () => {
      if (currentCardRef) {
        observer.unobserve(currentCardRef);
      }
    };
  }, []);

  // Only load video metadata when needed and in view
  useEffect(() => {
    if (videoRef.current && convertedVideoUrl && isInView) {
      // Only load metadata, don't buffer the entire video
      videoRef.current.load();
    }
  }, [convertedVideoUrl, isInView]);

  // Generate thumbnail from video if no thumbnail provided (debounced, only when in view)
  useEffect(() => {
    if (!stockVideo.thumbnailUrl && convertedVideoUrl && !generatedThumbnail && !thumbnailError && isInView) {
      // Debounce thumbnail generation to avoid overwhelming the browser
      const timer = setTimeout(() => {
        generateThumbnailFromVideo();
      }, 100); // Small delay to batch operations
      
      return () => clearTimeout(timer);
    }
  }, [stockVideo.thumbnailUrl, convertedVideoUrl, generatedThumbnail, thumbnailError, isInView]);

  // Preload thumbnail image only when in view
  useEffect(() => {
    const thumbnailUrl = stockVideo.thumbnailUrl || generatedThumbnail;
    if (thumbnailUrl && !isImagePreloaded && isInView) {
      const img = new Image();
      img.src = getOptimizedImageUrl(thumbnailUrl);
      img.onload = () => {
        setIsImagePreloaded(true);
      };
      img.onerror = () => {
        setThumbnailError(true);
        setIsImagePreloaded(true);
      };
    }
  }, [stockVideo.thumbnailUrl, generatedThumbnail, isImagePreloaded, isInView]);

  const generateThumbnailFromVideo = async () => {
    try {
      const video = document.createElement('video');
      video.crossOrigin = 'anonymous';
      video.muted = true;
      video.playsInline = true;
      
      const thumbnailPromise = new Promise((resolve, reject) => {
        video.onloadeddata = () => {
          try {
            const seekTime = Math.min(video.duration * 0.25, 0.5);
            video.currentTime = seekTime;
          } catch (err) {
            reject(new Error('Failed to seek video'));
          }
        };
        
        video.onseeked = () => {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            
            const ctx = canvas.getContext('2d');
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            
            // Use WebP if supported, otherwise JPEG with optimized quality
            const format = supportsWebP() ? 'image/webp' : 'image/jpeg';
            const quality = supportsWebP() ? 0.9 : 0.8;
            const thumbnailDataUrl = canvas.toDataURL(format, quality);
            resolve(thumbnailDataUrl);
            
            video.pause();
            if (video.src.startsWith('blob:')) {
              URL.revokeObjectURL(video.src);
            }
          } catch (err) {
            reject(new Error('Failed to generate thumbnail'));
          }
        };
        
        video.onerror = () => {
          reject(new Error('Failed to load video'));
          if (video.src.startsWith('blob:')) {
            URL.revokeObjectURL(video.src);
          }
        };
      });

      video.src = convertedVideoUrl;
      video.load();

      const thumbnailUrl = await thumbnailPromise;
      setGeneratedThumbnail(thumbnailUrl);
    } catch (error) {
      console.error('Error generating thumbnail:', error);
      setThumbnailError(true);
    }
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
    
    // Clear any existing timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    
    // Start playing video immediately (no delay)
    if (videoRef.current && convertedVideoUrl) {
      videoRef.current.currentTime = 0;
      const playPromise = videoRef.current.play();
      if (playPromise) {
        playPromise.catch((error) => {
          console.log('Video play failed:', error);
          setVideoError(true);
        });
      }
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    
    // Clear hover timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    
    // Pause video
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  const handleSaveClick = (e) => {
    e.stopPropagation();
    if (!isPremiumUser) return; // Only Premium users can save videos
    onSaveStockVideo(stockVideo.id, !isSaved);
  };

  const handleCardClick = () => {
    if (!isPremiumUser) {
      // Redirect Basic users to account page for upgrade
      navigate('/app/account', { 
        state: { 
          fromFeature: true,
          returnPath: '/app/stockVideos',
          featureName: 'Stock Videos'
        } 
      });
      return;
    }
    
    // Only Premium users can view videos
    onStockVideoClick(stockVideo);
  };

  const handleImageLoad = () => {
    if (!imageLoaded) {
      setImageLoaded(true);
      if (onImageLoaded) {
        onImageLoaded();
      }
    }
  };

  const handleImageError = () => {
    console.error(`Failed to load thumbnail for stock video: ${stockVideo.id}`);
    setThumbnailError(true);
    setImageLoaded(true);
    if (onImageLoaded) {
      onImageLoaded();
    }
  };

  // Force image loading check after a timeout to prevent hanging cards
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!imageLoaded) {
        console.log(`Timeout loading thumbnail for stock video: ${stockVideo.id}`);
        setImageLoaded(true);
        if (onImageLoaded) {
          onImageLoaded();
        }
      }
    }, 5000);
    
    return () => clearTimeout(timer);
  }, [stockVideo.id, imageLoaded, onImageLoaded]);

  // Clean up timeouts
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  const thumbnailSrc = getOptimizedImageUrl(stockVideo.thumbnailUrl || generatedThumbnail);

  return (
    <div 
      ref={cardRef}
      className="group relative transition-all duration-200 ease-in-out cursor-pointer"
      onClick={handleCardClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Video/Thumbnail container */}
      <div className="relative overflow-hidden rounded-lg">
        {/* Skeleton loader shown while image is loading */}
        {!isImagePreloaded && (
          <div className="bg-gray-200 dark:bg-gray-700 animate-pulse w-full aspect-video">
          </div>
        )}

        {/* Video - allow premium users to see videos play */}
        {convertedVideoUrl && isPremiumUser && (
          <video
            ref={videoRef}
            className={`absolute inset-0 w-full h-full object-cover z-10 ${
              isHovered ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
            style={{ transition: 'opacity 0.2s ease-in-out' }}
            muted
            playsInline
            loop
            preload="metadata"
            onError={() => setVideoError(true)}
            onLoadedMetadata={() => setVideoReady(true)}
          >
            <source src={convertedVideoUrl} type="video/mp4" />
          </video>
        )}
        
        {/* Thumbnail */}
        {!thumbnailError && thumbnailSrc ? (
          <div 
            className={`relative ${isImagePreloaded ? 'opacity-100' : 'opacity-0'} ${
              isHovered && convertedVideoUrl && !videoError && isPremiumUser ? 'opacity-0' : 'opacity-100'
            }`} 
            style={{ 
              transition: 'opacity 0.2s ease-in-out',
              visibility: isImagePreloaded ? 'visible' : 'hidden' 
            }}
          >
            <img
              src={thumbnailSrc}
              alt={stockVideo.title}
              className="w-full h-auto display-block"
              onLoad={handleImageLoad}
              onError={handleImageError}
            />
          </div>
        ) : (
          <div className="bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-center p-4 aspect-video">
          </div>
        )}
        
        {/* Heart/Save button in top right - only show for premium users */}
        {isPremiumUser && (
          <div className={`absolute top-2 right-2 sm:top-4 sm:right-4 flex space-x-2 sm:space-x-3 z-30 ${!isHovered && !isSaved ? 'hidden' : ''}`}>
            {(isHovered || isSaved) && (
              <button 
                onClick={handleSaveClick}
                className={`p-2 sm:p-2.5 rounded-full shadow-sm hover:shadow-md transition-all duration-150 hover:scale-110 ${
                  isSaved ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white' : 'bg-white text-gray-700'
                } ${!isHovered && !isSaved ? 'animate-fadeIn' : ''}`}
                style={!isHovered && isSaved ? {} : { animation: 'fadeIn 0.2s ease-out' }}
                aria-label={isSaved ? "Remove from saved" : "Save video"}
              >
                <Heart className={`w-4 h-4 sm:w-5 sm:h-5 ${isSaved ? 'fill-current' : ''}`} />
              </button>
            )}
          </div>
        )}
        
        {/* Bottom title - appears on hover for premium users */}
        {isPremiumUser && isHovered && imageLoaded && (
          <div className="absolute bottom-0 left-0 right-0 px-5 py-4 z-30 animate-fadeIn"
               style={{ animation: 'fadeIn 0.2s ease-out' }}>
            <p className="text-base font-medium truncate text-white">
              {stockVideo.title}
            </p>
          </div>
        )}
        
        {/* Premium upgrade overlay for Basic users */}
        {!isPremiumUser && isHovered && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-30 animate-fadeIn"
               style={{ animation: 'fadeIn 0.2s ease-out' }}>
            <div className="text-center">
              <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full p-3 mb-3 mx-auto w-fit">
                <Crown className="w-6 h-6" />
              </div>
              <p className="text-white text-sm font-medium mb-2">Upgrade to Premium</p>
              <p className="text-white/80 text-xs">Access stock videos</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

const StockVideoGrid = React.memo(function StockVideoGrid({ 
  stockVideos = [], 
  onStockVideoClick, 
  onSaveStockVideo, 
  savedStockVideoIds = new Set(), 
  loading = false, 
  skeletonItems = [],
  isPremiumUser = true
}) {
  const [columns, setColumns] = useState(4); // Default to 4 columns
  const gridRef = useRef(null);
  const [loadedImages, setLoadedImages] = useState(new Set());

  // Set up columns based on screen size
  useEffect(() => {
    const updateColumns = () => {
      const width = window.innerWidth;
      if (width < 1024) {
        setColumns(2); // Below lg: 2 columns
      } else {
        setColumns(3); // lg and up: 3 columns
      }
    };

    // Initial column setup
    updateColumns();

    // Update columns on resize
    window.addEventListener('resize', updateColumns);
    return () => window.removeEventListener('resize', updateColumns);
  }, []);

  // Track image loading progress
  useEffect(() => {
    if (stockVideos.length === 0) return;
    
    // Reset loaded images when stockVideos change
    setLoadedImages(new Set());
  }, [stockVideos]);

  // Handle image load events from StockVideoCard
  const handleImageLoaded = (stockVideoId) => {
    setLoadedImages(prev => {
      const newSet = new Set(prev);
      newSet.add(stockVideoId);
      return newSet;
    });
  };

  if (stockVideos.length === 0 && !loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
          <Play className="w-8 h-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No videos found</h3>
        <p className="text-gray-500 dark:text-gray-400 max-w-md">
          Try adjusting your search criteria or filters to find the stock videos you're looking for.
        </p>
      </div>
    );
  }

  // Distribute stock videos across columns for masonry layout
  const distributeStockVideos = () => {
    const columnArrays = Array.from({ length: columns }, () => []);
    
    stockVideos.forEach((stockVideo, index) => {
      const columnIndex = index % columns;
      columnArrays[columnIndex].push(stockVideo);
    });
    
    return columnArrays;
  };
  
  const columnArrays = distributeStockVideos();

  return (
    <div className="masonry-wrapper">
      <div className="masonry-grid" ref={gridRef}>
        {columnArrays.map((columnStockVideos, columnIndex) => (
          <div 
            key={`column-${columnIndex}`} 
            className="masonry-column"
            style={{ width: `${100 / columns}%` }}
          >
            {columnStockVideos.map((stockVideo) => (
              <div 
                key={stockVideo.id} 
                className="masonry-item sm:p-1"
              >
                <div className="masonry-item-inner">
                  <StockVideoCard 
                    stockVideo={stockVideo} 
                    onStockVideoClick={onStockVideoClick}
                    onSaveStockVideo={onSaveStockVideo}
                    isSaved={savedStockVideoIds.has(stockVideo.id)}
                    onImageLoaded={() => handleImageLoaded(stockVideo.id)}
                    isPremiumUser={isPremiumUser}
                  />
                </div>
              </div>
            ))}
            
            {/* Skeleton items for loading */}
            {loading && skeletonItems.map((_, index) => (
              <div 
                key={`skeleton-${columnIndex}-${index}`} 
                className="masonry-item sm:p-1"
              >
                <div className="masonry-item-inner bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse">
                  <div className="w-full aspect-video bg-gradient-to-b from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-800 rounded-sm"></div>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
});

export default StockVideoGrid; 